import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Users, Edit } from 'lucide-react';
import { format } from 'date-fns';

const AdminRideManagement = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ridesRes, driversRes] = await Promise.all([
        axios.get(`${API}/rides`),
        axios.get(`${API}/users`)
      ]);
      setRides(ridesRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openAssignDialog = (ride) => {
    setSelectedRide(ride);
    setSelectedDriver('');
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedDriver) {
      toast.error('Please select a driver');
      return;
    }

    try {
      await axios.post(`${API}/rides/${selectedRide.id}/assign`, {
        driver_id: selectedDriver
      });
      toast.success('Ride assigned successfully');
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign ride');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      available: { color: 'bg-green-100 text-green-700 border-green-300', label: 'Available' },
      assigned: { color: 'bg-blue-100 text-blue-700 border-blue-300', label: 'Assigned' },
      claimed: { color: 'bg-purple-100 text-purple-700 border-purple-300', label: 'Claimed' },
      completed: { color: 'bg-gray-100 text-gray-700 border-gray-300', label: 'Completed' }
    };
    const variant = variants[status] || variants.available;
    return <Badge className={variant.color}>{variant.label}</Badge>;
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? driver.name : 'Unassigned';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-ride-management-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Ride Management</h2>
          <p className="text-gray-600 mt-1">Assign rides to drivers or post as available</p>
        </div>
        <Button onClick={() => navigate('/rides/new')} data-testid="create-ride-button">
          Create New Ride
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Total Rides</div>
            <div className="text-2xl font-bold">{rides.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Available</div>
            <div className="text-2xl font-bold text-green-600">
              {rides.filter(r => r.status === 'available').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 mb-1">Drivers</div>
            <div className="text-2xl font-bold">{drivers.length}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Rides</CardTitle>
        </CardHeader>
        <CardContent>
          {rides.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No rides yet</p>
          ) : (
            <div className="space-y-3">
              {rides.map((ride) => (
                <div
                  key={ride.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`admin-ride-${ride.id}`}
                >
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <div>
                          <span className="font-semibold">{ride.client_name}</span>
                          <span className="text-sm text-gray-600 ml-2">{ride.client_phone}</span>
                        </div>
                        {getStatusBadge(ride.status)}
                        {ride.available_for_claim && (
                          <Badge className="bg-gold-100 text-gold-700 border-gold-300">
                            Can Claim
                          </Badge>
                        )}
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        {format(new Date(ride.date), 'MMM dd, yyyy h:mm a')}
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        {ride.pickup_location} → {ride.dropoff_location}
                      </div>
                      
                      {ride.assigned_to && (
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="w-4 h-4 text-blue-600" />
                          <span className="text-blue-600 font-medium">
                            {getDriverName(ride.assigned_to)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-end justify-between gap-2 sm:min-w-[140px]">
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">
                          ${ride.net_earnings.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">net earnings</div>
                      </div>
                      
                      {ride.status !== 'completed' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/rides/edit/${ride.id}`)}
                            data-testid={`edit-ride-${ride.id}`}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAssignDialog(ride)}
                            data-testid={`assign-ride-${ride.id}`}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            {ride.assigned_to ? 'Reassign' : 'Assign'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Ride to Driver</DialogTitle>
            <DialogDescription>
              Select a driver to assign this ride to {selectedRide?.client_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Select Driver</label>
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger data-testid="driver-select">
                  <SelectValue placeholder="Choose a driver" />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name} ({driver.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setAssignDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                className="flex-1"
                data-testid="confirm-assign-button"
              >
                Assign Ride
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRideManagement;
